import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllBuyGetDiscountOffersApi,
  deleteBuyGetDiscountOfferApi,
  toggleBuyGetDiscountOfferStatusApi,
} from "@/api/buyGetDiscountOffer";

export const fetchBuyGetDiscountOffers = createAsyncThunk(
  "buyGetDiscountOffer/fetchAll",
  async (
    { restaurant_id, page = 1, search = "", status = "", per_page = 10 },
    { rejectWithValue }
  ) => {
    const params = new URLSearchParams();
    params.append("per_page", per_page);
    params.append("page", page);

    if (search) {
      params.append("search_term", search);
    }
    if (status && status !== "0") {
      params.append("status", status);
    }

    const res = await getAllBuyGetDiscountOffersApi(restaurant_id, params.toString());

    if (res.status === "success") {
      return { ...res.data, search };
    }

    return rejectWithValue(res.message);
  }
);

export const deleteBuyGetDiscountOfferById = createAsyncThunk(
  "buyGetDiscountOffer/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteBuyGetDiscountOfferApi(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

export const toggleBuyGetDiscountOfferStatusById = createAsyncThunk(
  "buyGetDiscountOffer/toggleStatus",
  async (id, { rejectWithValue }) => {
    const res = await toggleBuyGetDiscountOfferStatusApi(id);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
  }
);

const initialState = {
  offers: [],
  singleOffer: null,
  totalPages: 1,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,
  toggleStatusLoading: false,

  success: null,
  error: null,
  requestStatus: false,
  search: "",
  status: "",
  perPage: 10,
  currentPage: 1,
};

const buyGetDiscountOfferSlice = createSlice({
  name: "buyGetDiscountOffer",
  initialState,
  reducers: {
    clearBuyGetDiscountOfferMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
    setOfferSearch: (state, action) => {
      state.search = action.payload;
    },
    setStatusFilter: (state, action) => {
      state.status = action.payload;
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchBuyGetDiscountOffers.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchBuyGetDiscountOffers.fulfilled, (state, action) => {
        state.getLoading = false;
        state.offers = action.payload.offers || [];
        if (action.payload.pagination) {
          state.totalPages = action.payload.pagination.last_page || 1;
        } else {
          state.totalPages = 1;
        }
      })
      .addCase(fetchBuyGetDiscountOffers.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
      })

      // Delete
      .addCase(deleteBuyGetDiscountOfferById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
      })
      .addCase(deleteBuyGetDiscountOfferById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.offers = state.offers.filter(
          (offer) => offer.id !== action.payload
        );
        state.success = "Offer deleted successfully!";
      })
      .addCase(deleteBuyGetDiscountOfferById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
      })

      // Toggle Status
      .addCase(toggleBuyGetDiscountOfferStatusById.pending, (state) => {
        state.toggleStatusLoading = true;
      })
      .addCase(toggleBuyGetDiscountOfferStatusById.fulfilled, (state, action) => {
        state.toggleStatusLoading = false;
        const updatedOffer = action.payload;
        if (updatedOffer && updatedOffer.id) {
          state.offers = state.offers.map((offer) =>
            offer.id === updatedOffer.id
              ? { ...offer, status: updatedOffer.status }
              : offer
          );
        }
        state.success = "Status updated successfully!";
      })
      .addCase(toggleBuyGetDiscountOfferStatusById.rejected, (state, action) => {
        state.toggleStatusLoading = false;
        state.error = action.payload;
      });
  },
});

export const {
  clearBuyGetDiscountOfferMessages,
  setOfferSearch,
  setStatusFilter,
  setPerPage,
  setCurrentPage,
} = buyGetDiscountOfferSlice.actions;

export default buyGetDiscountOfferSlice.reducer;
